/**
 * پل اپ اندروید
 * ==============
 * سایت و اپ یک کد دارند؛ این فایل تنها جایی است که تفاوتشان را می‌داند.
 *
 * وقتی صفحه داخل اپ اجرا شود، پوسته اندروید یک شیء به نام `ArzinoApp` روی
 * `window` می‌گذارد. از روی آن می‌شود امکاناتی را نشان داد که فقط در اپ معنا
 * دارند (قفل اثر انگشت، لرزش، اشتراک‌گذاری بومی) و چیزهایی را پنهان کرد که
 * در اپ بی‌معنی‌اند (دکمه «دانلود اپلیکیشن»).
 *
 * همه چیز اینجا **دفاعی** نوشته شده: اگر پل نبود، نسخه قدیمی اپ بود، یا متدی
 * وجود نداشت، هیچ‌کدام نباید صفحه را بشکنند. سایت باید در مرورگر معمولی دقیقاً
 * مثل قبل کار کند.
 */

type Bridge = {
  info?: () => string
  setBiometric?: (enabled: boolean) => void
  setBlockScreenshots?: (enabled: boolean) => void
  share?: (title: string, text: string) => void
  haptic?: () => void
}

export type NativeInfo = {
  isApp: boolean
  platform: string
  version: string
  versionCode: number
  biometricAvailable: boolean
  biometricEnabled: boolean
  blockScreenshots: boolean
}

const EMPTY: NativeInfo = {
  isApp: false, platform: 'web', version: '', versionCode: 0,
  biometricAvailable: false, biometricEnabled: false, blockScreenshots: false,
}

function bridge(): Bridge | null {
  if (typeof window === 'undefined') return null
  return (window as any).ArzinoApp ?? null
}

/** آیا این صفحه داخل اپ اندروید باز شده است؟ */
export const isNativeApp = (): boolean => !!bridge()

/**
 * اطلاعات اپ.
 *
 * یک بار خوانده و نگه داشته می‌شود: هر صدا زدن پل، یک پرش بین جاوااسکریپت و
 * کاتلین است و در فهرست‌هایی که هر ثانیه دوباره رسم می‌شوند، هزینه‌اش دیده
 * می‌شود.
 */
let cached: NativeInfo | null = null
export function nativeInfo(force = false): NativeInfo {
  if (cached && !force) return cached
  const b = bridge()
  if (!b?.info) return (cached = EMPTY)
  try {
    cached = { ...EMPTY, ...JSON.parse(b.info()) }
  } catch {
    cached = EMPTY
  }
  return cached!
}

/** روشن/خاموش کردن قفل اثر انگشت اپ. خروجی: آیا اعمال شد؟ */
export function setBiometric(enabled: boolean): boolean {
  const b = bridge()
  if (!b?.setBiometric) return false
  try { b.setBiometric(enabled); nativeInfo(true); return true } catch { return false }
}

/** جلوگیری از عکس‌گرفتن از صفحه (پیش‌فرض روشن). */
export function setBlockScreenshots(enabled: boolean): boolean {
  const b = bridge()
  if (!b?.setBlockScreenshots) return false
  try { b.setBlockScreenshots(enabled); nativeInfo(true); return true } catch { return false }
}

/**
 * اشتراک‌گذاری با اپ‌های گوشی.
 *
 * سه لایه: پل بومی → Web Share API → کپی در کلیپ‌بورد. کاربر در هر سه حالت
 * نتیجه می‌گیرد، فقط با تجربه‌ای که آن محیط اجازه می‌دهد.
 */
export async function share(title: string, text: string): Promise<'native' | 'web' | 'copy' | 'none'> {
  const b = bridge()
  if (b?.share) { try { b.share(title, text); return 'native' } catch { /* ادامه */ } }
  const nav: any = typeof navigator !== 'undefined' ? navigator : null
  if (nav?.share) {
    try { await nav.share({ title, text }); return 'web' } catch { /* کاربر لغو کرد */ }
  }
  if (nav?.clipboard?.writeText) {
    try { await nav.clipboard.writeText(text); return 'copy' } catch { /* ادامه */ }
  }
  return 'none'
}

/** لرزش کوتاه تأیید — مثلاً پس از ثبت سفارش. */
export function haptic(): void {
  const b = bridge()
  if (b?.haptic) { try { b.haptic(); return } catch { /* ادامه */ } }
  try { (navigator as any)?.vibrate?.(18) } catch { /* بی‌اهمیت */ }
}
